import type {
    EngagementStats,
    VoteType,
} from "@mrflark/spl_sdk";
import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    Button,
} from "@mrflark/ui";
import {
    ArrowLeft,
    Eye,
    Heart,
    Sparkles,
    ThumbsDown,
    ThumbsUp,
} from "lucide-preact";
import {
    type ReactElement,
    useCallback,
    useEffect,
    useState,
} from "preact/compat";
import {
    useNavigate,
    useParams,
} from "react-router-dom";
import {
    splClient,
} from "../api/client.ts";

const TABS = [
    {
        id: "liked" as const,
        label: "Liked",
        icon: ThumbsUp,
        voteFilter: "like" as VoteType,
    },
    {
        id: "loved" as const,
        label: "Loved",
        icon: Heart,
        voteFilter: "love" as VoteType,
    },
    {
        id: "disliked" as const,
        label: "Disliked",
        icon: ThumbsDown,
        voteFilter: "dislike" as VoteType,
    },
    {
        id: "viewed" as const,
        label: "Recently Viewed",
        icon: Eye,
        voteFilter: undefined,
    },
    {
        id: "recommended" as const,
        label: "Recommendations",
        icon: Sparkles,
        voteFilter: undefined,
    },
] as const;

type TabId = (typeof TABS)[number]["id"];

export default function ProfilePage(): ReactElement {
    const {
        userId,
    } = useParams<{ userId: string; }>();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabId>("liked");
    const [stats, setStats] = useState<EngagementStats | null>(null);
    const [items, setItems] = useState<MediaItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!userId) return;
        splClient.getProfileStats(userId)
            .then((result) => setStats(result))
            .catch(() => {/* ignore */});
    }, [userId]);

    const fetchTabContent = useCallback(
        async (tab: TabId, cursor?: string): Promise<void> => {
            if (!userId) return;
            setLoading(true);

            try {
                const tabConfig = TABS.find((tabEntry) => tabEntry.id === tab);

                if (tab === "viewed") {
                    const result = await splClient.getProfileViews(userId, {
                        cursor,
                        limit: 20,
                    });
                    if (cursor) {
                        setItems((prev) => [...prev, ...result.items]);
                    } else {
                        setItems(result.items);
                    }
                    setHasMore(result.hasMore);
                    setNextCursor(result.nextCursor);
                } else if (tab === "recommended") {
                    const result = await splClient.getProfileRecommendations(
                        userId,
                        20,
                    );
                    setItems(result.items);
                    setHasMore(false);
                    setNextCursor(undefined);
                } else if (tabConfig?.voteFilter) {
                    const result = await splClient.getProfileVotes(userId, {
                        vote: tabConfig.voteFilter,
                        cursor,
                        limit: 20,
                    });
                    if (cursor) {
                        setItems((prev) => [...prev, ...result.items]);
                    } else {
                        setItems(result.items);
                    }
                    setHasMore(result.hasMore);
                    setNextCursor(result.nextCursor);
                }
            } catch {
                /* ignore */
            } finally {
                setLoading(false);
            }
        },
        [userId],
    );

    useEffect(() => {
        setItems([]);
        setHasMore(false);
        setNextCursor(undefined);
        fetchTabContent(activeTab);
    }, [activeTab, fetchTabContent]);

    const handleLoadMore = (): void => {
        if (nextCursor) {
            fetchTabContent(activeTab, nextCursor);
        }
    };

    const handleItemClick = (item: MediaItem): void => {
        navigate(`/p/${item.id}`);
    };

    return (
        <div className="min-h-screen bg-bg-primary text-text-primary">
            <div className="max-w-5xl mx-auto px-6 py-8">
                {/* header */}
                <div className="flex items-center gap-4 mb-8">
                    <Button variant="secondary" onClick={() => navigate("/")}>
                        <ArrowLeft size={16} />
                        Back
                    </Button>
                    <h1 className="text-2xl font-bold">Profile</h1>
                </div>

                {/* stats bar */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
                        <div className="bg-bg-secondary rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-accent">
                                {stats.totalVotes}
                            </div>
                            <div className="text-xs text-text-secondary mt-1">
                                Total Votes
                            </div>
                        </div>
                        <div className="bg-bg-secondary rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-success">
                                {stats.likes}
                            </div>
                            <div className="text-xs text-text-secondary mt-1">
                                Likes
                            </div>
                        </div>
                        <div className="bg-bg-secondary rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-pink-400">
                                {stats.loves}
                            </div>
                            <div className="text-xs text-text-secondary mt-1">
                                Loves
                            </div>
                        </div>
                        <div className="bg-bg-secondary rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-text-primary">
                                {stats.totalViewed}
                            </div>
                            <div className="text-xs text-text-secondary mt-1">
                                Viewed
                            </div>
                        </div>
                        <div className="bg-bg-secondary rounded-lg p-4 text-center">
                            <div className="text-sm font-medium text-text-primary truncate">
                                {stats.topTags.length > 0
                                    ? stats.topTags[0].tag
                                    : "-"}
                            </div>
                            <div className="text-xs text-text-secondary mt-1">
                                Top Tag
                            </div>
                        </div>
                    </div>
                )}

                {/* tab bar */}
                <div className="flex gap-1 mb-6 overflow-x-auto border-b border-border">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                type="button"
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                                    activeTab === tab.id
                                        ? "border-accent text-accent"
                                        : "border-transparent text-text-secondary hover:text-text-primary"
                                }`}
                            >
                                <Icon size={16} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* content area */}
                {loading && items.length === 0
                    ? (
                        <div className="text-center text-text-secondary py-16">
                            Loading...
                        </div>
                    )
                    : items.length === 0
                    ? (
                        <div className="text-center text-text-muted py-16">
                            No items found for this tab.
                        </div>
                    )
                    : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                {items.map((item) => (
                                    <button
                                        type="button"
                                        key={item.id}
                                        onClick={() => handleItemClick(item)}
                                        className="group relative aspect-square rounded-lg overflow-hidden bg-bg-secondary hover:ring-2 hover:ring-accent transition-all"
                                    >
                                        {item.media_type === "video"
                                            ? (
                                                <video
                                                    src={item.thumbnail_url ||
                                                        item.media_url}
                                                    className="w-full h-full object-cover"
                                                    muted
                                                />
                                            )
                                            : (
                                                <img
                                                    src={item.thumbnail_url ||
                                                        item.media_url}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                            )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <span className="text-xs text-white truncate block">
                                                {item.tags?.slice(0, 2).join(
                                                    ", ",
                                                ) || ""}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {hasMore && (
                                <div className="flex justify-center mt-8">
                                    <Button
                                        variant="secondary"
                                        onClick={handleLoadMore}
                                        disabled={loading}
                                    >
                                        {loading ? "Loading..." : "Load More"}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
            </div>
        </div>
    );
}
